import { useCallback, useEffect, useRef, useState } from "react";

import type { FormHandle } from "@performyard/py-components";

import { useAskPerformYardStore } from "../../../../containers/AskPerformYard/AskPerformYardStore";
import type { FormImprovement } from "../../../../containers/AskPerformYard/types";

import { useEditFormStore } from "./useEditFormStore";

import type { ReviewQuestion, ReviewQuestionSchema } from "./utils";

type CoachField = FormImprovement["field"];

const CHOICE_QUESTION_TYPES = ["multiple-choice", "multiple-select"];

/** Merge an improvement into a question, or return null when it can't apply
 *  (payload missing, or the question's type doesn't carry the targeted field). */
const applyImprovementToQuestion = (question: ReviewQuestion, improvement: FormImprovement): ReviewQuestion | null => {
	switch (improvement.field) {
		case "question":
			return { ...question, question: improvement.suggestedValue };
		case "subtext":
			return { ...question, subtext: improvement.suggestedValue };
		case "choices": {
			if (!CHOICE_QUESTION_TYPES.includes(question.type) || !improvement.suggestedChoices?.length) return null;
			return { ...question, choices: improvement.suggestedChoices };
		}
		case "ratingScale": {
			if (question.type !== "rating-scale" || !improvement.suggestedRatingScale?.length) return null;
			return {
				...question,
				choices: improvement.suggestedRatingScale.map((option) => ({
					value: option.value,
					name: option.name,
					description: option.description ?? "",
				})),
			};
		}
	}
};

export type EditQuestionCoachProps = {
	/** Per-field flash trigger token passed to flashKey-capable inputs (PY-13212). */
	flashKeys: Partial<Record<CoachField, number>>;
	/** Per-field "Improved with AI" label flags for the currently open card. */
	improvedFields: Partial<Record<CoachField, boolean>>;
	/** Clear the "Improved with AI" label once the user edits the field themselves. */
	onImprovedFieldEdit: (field: CoachField) => void;
};

type UseFormBuildingCoachResult = {
	/** Key for the edit card — changes on apply so PYForm remounts with the merged values. */
	editCardKey: (refId: string) => string;
	/** The question with the pending improvement merged in (form defaults only — the store
	 *  stays untouched until the user saves, so Cancel discards the change). */
	questionWithImprovement: (question: ReviewQuestion) => ReviewQuestion;
	coach: EditQuestionCoachProps;
};

/**
 * Bridges the form builder to the Form Building Coach: registers the "Apply Change"
 * applier (auto-saves any open card, opens the target card pre-filled with the
 * suggestion) and tracks per-field flash/label state for the open card.
 */
export const useFormBuildingCoach = (
	activeQuestionFormRef: React.RefObject<FormHandle<typeof ReviewQuestionSchema> | null>
): UseFormBuildingCoachResult => {
	const setFormImprovementApplier = useAskPerformYardStore((s) => s.setFormImprovementApplier);
	const editId = useEditFormStore((s) => s.editingQuestion?.refId);

	const [pending, setPending] = useState<{ refId: string; improvement: FormImprovement; version: number } | null>(
		null
	);
	const [flashKeys, setFlashKeys] = useState<Partial<Record<CoachField, number>>>({});
	const [improvedFields, setImprovedFields] = useState<Partial<Record<CoachField, boolean>>>({});
	const versionRef = useRef(0);

	// Reassigned each render so the registered applier reads the latest form ref.
	const applyRef = useRef<(improvement: FormImprovement) => Promise<boolean>>(() => Promise.resolve(false));
	applyRef.current = async (improvement: FormImprovement) => {
		const { questions, editingQuestion } = useEditFormStore.getState();
		const storeTarget = questions.find((q) => q.refId === improvement.questionId);
		// When the target is the open card, gate compatibility on its live form values —
		// the snapshot sent to the coach merges them too, so an unsaved type change can
		// legitimately produce an improvement the last-saved question would reject.
		const liveValues =
			editingQuestion?.refId === improvement.questionId
				? (activeQuestionFormRef.current?.getFormValues() ?? null)
				: null;
		const target = storeTarget && liveValues ? { ...storeTarget, ...liveValues } : storeTarget;
		if (!target || applyImprovementToQuestion(target, improvement) === null) return false;

		// A card is open (possibly the target itself): save it first — the same contract
		// as clicking Edit on another card. A validation failure blocks the apply.
		if (editingQuestion?.refId && activeQuestionFormRef.current) {
			const isValid = await activeQuestionFormRef.current.validate(true);
			if (!isValid) return false;
			await activeQuestionFormRef.current.submit();
		}

		useEditFormStore.getState().setEditingQuestion(improvement.questionId);
		versionRef.current += 1;
		setPending({ refId: improvement.questionId, improvement, version: versionRef.current });
		setImprovedFields({ [improvement.field]: true });

		// Defer to after the remounted edit card paints: the flash-key bump must be a
		// post-mount change (the initial key never flashes) and the smooth scroll needs
		// the final layout (double rAF runs post-paint).
		const { field, questionId } = improvement;
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				setFlashKeys((prev) => ({ ...prev, [field]: (prev[field] ?? 0) + 1 }));
				document
					.getElementById(`question-${questionId}`)
					?.scrollIntoView({ behavior: "smooth", block: "center" });
			});
		});
		return true;
	};

	useEffect(() => {
		const stableApply = (improvement: FormImprovement): Promise<boolean> => applyRef.current(improvement);
		setFormImprovementApplier(stableApply);
		return () => setFormImprovementApplier(null);
	}, [setFormImprovementApplier]);

	useEffect(() => {
		useEditFormStore
			.getState()
			.setActiveQuestionValuesReader(() => activeQuestionFormRef.current?.getFormValues() ?? null);
		return () => useEditFormStore.getState().setActiveQuestionValuesReader(null);
	}, [activeQuestionFormRef]);

	// Once the improved card is saved, cancelled, or switched away, drop the pending
	// merge and labels — a reopened card must not show the badge again.
	useEffect(() => {
		if (pending && editId !== pending.refId) {
			setPending(null);
			setImprovedFields({});
		}
	}, [editId, pending]);

	const onImprovedFieldEdit = useCallback((field: CoachField) => {
		setImprovedFields((prev) => {
			if (!prev[field]) return prev;
			return { ...prev, [field]: false };
		});
	}, []);

	const questionWithImprovement = useCallback(
		(question: ReviewQuestion): ReviewQuestion => {
			if (!pending || question.refId !== pending.refId) return question;
			return applyImprovementToQuestion(question, pending.improvement) ?? question;
		},
		[pending]
	);

	const editCardKey = useCallback(
		(refId: string): string => (pending?.refId === refId ? `${refId}:coach-${pending.version}` : refId),
		[pending]
	);

	return { editCardKey, questionWithImprovement, coach: { flashKeys, improvedFields, onImprovedFieldEdit } };
};
